import { Alert, AlertLabels, AlertInstance } from '../types/alerts';

/**
 * Clean up alert description to show summary instead of long lists
 * @param description - The raw alert description
 * @param instances - The parsed instances array
 * @returns Clean description
 */
export function cleanAlertDescription(description: string | null | undefined, instances: AlertInstance[] = []): string {
  if (!description) return '';
  // If description starts with "Affected:" and has a long list, replace with summary
  if (description.startsWith('Affected:')) {
    const instanceCount = instances.length;
    if (instanceCount > 0) {
      return `${instanceCount} ${instanceCount === 1 ? 'instance' : 'instances'} affected. Click to expand for details.`;
    }
  }
  
  // If description is very long (more than 150 chars), truncate it
  if (description.length > 150) {
    return description.substring(0, 147) + '...';
  }
  
  return description;
}

/**
 * Parse alert labels JSON and enhance alert with computed properties
 * @param alert - The raw alert from the API
 * @returns Enhanced alert with parsed instances and count
 */
export function parseAlert(alert: Alert): Alert {
  try {
    const labels: AlertLabels = JSON.parse(alert.labels);
    const instances = labels.instances || [];
    
    return {
      ...alert,
      instanceCount: labels.instance_count || instances.length || 0,
      instances,
      // Replace long descriptions with clean summaries
      description: cleanAlertDescription(alert.description, instances)
    };
  } catch (error) {
    console.warn('Failed to parse alert labels:', error);
    return {
      ...alert,
      instanceCount: extractInstanceCount(alert.summary) || 0,
      instances: [],
      description: cleanAlertDescription(alert.description)
    };
  }
}

/**
 * Parse multiple alerts and enhance them with computed properties
 * @param alerts - Array of raw alerts from the API
 * @returns Array of enhanced alerts
 */
export function parseAlerts(alerts: Alert[]): Alert[] {
  if (!Array.isArray(alerts)) {
    return [];
  }
  return alerts.map(parseAlert);
}

/**
 * Get instance count from alert summary
 * @param summary - Alert summary string like "BlackboxExporterDown (30 instances)"
 * @returns Instance count or 0 if not found
 */
export function extractInstanceCount(summary: string): number {
  const match = summary.match(/\((\d+)\s+instances?\)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Get alert type name without instance count
 * @param summary - Alert summary string like "BlackboxExporterDown (30 instances)"
 * @returns Clean alert name
 */
export function getCleanAlertName(summary: string): string {
  return summary.replace(/\s*\(\d+\s+instances?\)/, '');
}

/**
 * Format confirmation message for alert actions
 * @param action - The action being performed (acknowledge, dismiss, delete)
 * @param alert - The alert being acted upon
 * @returns Formatted confirmation message
 */
export function getActionConfirmationMessage(
  action: 'acknowledge' | 'dismiss' | 'delete',
  alert: Alert
): string {
  const alertName = getCleanAlertName(alert.summary);
  const instanceCount = alert.instanceCount || extractInstanceCount(alert.summary);
  const instances = instanceCount === 1 ? 'instance' : 'instances';
  
  switch (action) {
    case 'acknowledge':
      return `Are you sure you want to acknowledge "${alertName}" affecting ${instanceCount} ${instances}?`;
    case 'dismiss':
      return `Are you sure you want to dismiss "${alertName}" affecting ${instanceCount} ${instances}? This will mark all instances as resolved.`;
    case 'delete':
      return `Are you sure you want to permanently delete "${alertName}" affecting ${instanceCount} ${instances}? This action cannot be undone.`;
    default:
      return `Are you sure you want to ${action} this alert?`;
  }
}

/**
 * Get severity color for UI components
 * @param severity - Alert severity level
 * @returns CSS color string
 */
export function getSeverityColor(severity: Alert['severity']): string {
  switch (severity) {
    case 'critical': return '#f44336'; // red
    case 'error': return '#f44336'; // red
    case 'warning': return '#ff9800'; // orange
    case 'info': return '#2196f3'; // blue
    default: return '#9e9e9e'; // gray
  }
}

/**
 * Get status color for UI components
 * @param status - Alert status
 * @returns CSS color string
 */
export function getStatusColor(status: Alert['status']): string {
  switch (status) {
    case 'new': return '#f44336'; // red
    case 'acknowledged': return '#ff9800'; // orange
    case 'resolved': return '#4caf50'; // green
    default: return '#9e9e9e'; // gray
  }
}

/**
 * Format time string for display
 * @param timeString - ISO time string
 * @returns Formatted time string
 */
export function formatTime(timeString: string): string {
  return new Date(timeString).toLocaleString();
}

/**
 * Calculate duration between start and end times
 * @param startsAt - Start time ISO string
 * @param endsAt - End time ISO string (optional, uses current time if not provided)
 * @returns Human-readable duration string
 */
export function calculateDuration(startsAt: string, endsAt?: string): string {
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : new Date();
  const duration = end.getTime() - start.getTime();

  if (duration <= 0) {
    return '< 1m';
  }

  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}